import { useEffect, useState } from "react";
import {
  fetchStats,
  fetchMessages,
  fetchPauseStatus,
  pauseAutoReply,
  resumeAutoReply,
  fetchAlertFailures,
  redeliverMessage,
  deleteMessage,
} from "../lib/api";
import Shell from "../components/Shell";
import { MetricCard, PriorityBadge, KeywordPill, Input, Select, PrimaryButton, SecondaryButton } from "../components/ui-bits";
import AICostGuard from "../components/AICostGuard";
import { MagnifyingGlassIcon, ChatTeardropTextIcon, PauseIcon, PlayIcon, WarningOctagonIcon, ArrowClockwiseIcon, TrashIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const POLL_MS = 5000;

function formatTime(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return ts;
  }
}

function formatDate(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filterPriority, setFilterPriority] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [pause, setPause] = useState({ paused: false, seconds_remaining: 0 });
  const [failures, setFailures] = useState({ count: 0, items: [] });

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [s, m, p, f] = await Promise.all([
          fetchStats(),
          fetchMessages({ priority: filterPriority || undefined, q: search || undefined, limit: 100 }),
          fetchPauseStatus().catch(() => ({ paused: false, seconds_remaining: 0 })),
          fetchAlertFailures().catch(() => ({ count: 0, items: [] })),
        ]);
        if (!active) return;
        setStats(s);
        setMessages(m);
        setPause(p);
        setFailures(f);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [filterPriority, search]);

  const onRedeliver = async (mid) => {
    try {
      const r = await redeliverMessage(mid);
      if (r.ok) {
        toast.success("Alert redelivered");
        setFailures((prev) => ({ count: prev.count - 1, items: prev.items.filter((x) => x.id !== mid) }));
      } else {
        toast.error("All channels still failing — check config");
      }
    } catch {
      toast.error("Redeliver failed");
    }
  };

  const onDelete = async (mid) => {
    const prev = messages;
    setMessages((cur) => cur.filter((x) => x.id !== mid)); // optimistic
    try {
      await deleteMessage(mid);
      toast.success("Lead deleted");
    } catch {
      setMessages(prev); // rollback
      toast.error("Delete failed");
    }
  };

  const onPause = async () => {
    try {
      const r = await pauseAutoReply(24);
      setPause({ paused: true, seconds_remaining: 24 * 3600, paused_until: r.paused_until });
      toast.success("Auto-reply paused for 24 hours");
    } catch {
      toast.error("Failed to pause");
    }
  };

  const onResume = async () => {
    try {
      await resumeAutoReply();
      setPause({ paused: false, seconds_remaining: 0 });
      toast.success("Auto-reply resumed");
    } catch {
      toast.error("Failed to resume");
    }
  };

  const fmtRemaining = (s) => {
    if (!s) return "";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const ai = stats?.ai || {};

  return (
    <Shell
      title="Opportunity Feed"
      subtitle="Real-time freight leads detected across your monitored WhatsApp groups."
      actions={
        <div className="flex items-center gap-3 flex-wrap">
          {pause.paused ? (
            <PrimaryButton
              data-testid="kill-switch-resume"
              onClick={onResume}
              className="!bg-emerald-600 hover:!bg-emerald-700 flex items-center gap-2"
            >
              <PlayIcon size={16} weight="bold" />
              Resume auto-reply ({fmtRemaining(pause.seconds_remaining)} left)
            </PrimaryButton>
          ) : (
            <SecondaryButton
              data-testid="kill-switch-pause"
              onClick={onPause}
              className="!border-[#FF3B30] !text-[#FF3B30] hover:!bg-[#FF3B30]/5 flex items-center gap-2"
            >
              <PauseIcon size={16} weight="bold" />
              Pause auto-reply (24h)
            </SecondaryButton>
          )}
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span className="w-1.5 h-1.5 bg-emerald-500 live-dot" />
            Auto-refresh {POLL_MS / 1000}s
          </div>
        </div>
      }
    >
      {/* Delivery failure banner — only shows if recent HIGH leads had ALL channels fail */}
      {failures.count > 0 && (
        <div
          data-testid="delivery-failure-banner"
          className="border border-[#FF3B30] bg-[#FF3B30]/5 p-4 mb-6 flex items-start gap-3"
        >
          <WarningOctagonIcon size={22} weight="fill" className="text-[#FF3B30] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-sm text-[#FF3B30]">
              {failures.count} HIGH lead{failures.count > 1 ? "s" : ""} delivered through NO channel
            </div>
            <p className="text-xs text-zinc-700 mt-1">
              All of Pushover, Web Push and Email failed for these leads in the last 24 h. Check your
              channel configuration on Settings, then click <em>Redeliver</em> below to retry.
            </p>
            <div className="mt-3 space-y-2">
              {failures.items.slice(0, 5).map((m) => (
                <div
                  key={m.id}
                  data-testid={`failure-item-${m.id}`}
                  className="flex items-start justify-between gap-3 border border-zinc-200 bg-white p-3 text-xs"
                >
                  <div className="min-w-0 flex-1">
                    <div className="overline">{m.group_name}</div>
                    <p className="text-zinc-800 line-clamp-2">{m.message}</p>
                  </div>
                  <SecondaryButton
                    data-testid={`redeliver-${m.id}`}
                    onClick={() => onRedeliver(m.id)}
                    className="!px-2 !py-1 flex items-center gap-1 flex-shrink-0"
                  >
                    <ArrowClockwiseIcon size={12} weight="bold" />
                    Redeliver
                  </SecondaryButton>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI Cost Guard — kill switch + spend meter + editable monthly cap */}
      <AICostGuard
        spendEur={ai.cost_eur_est ?? 0}
        calls={ai.calls ?? 0}
      />

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard
          testId="metric-today-high"
          label="Today · High priority"
          value={stats?.today_high ?? "—"}
          sub={`${stats?.today_medium ?? 0} medium · ${stats?.today_total ?? 0} total`}
          accent="danger"
        />
        <MetricCard
          testId="metric-total-scored"
          label="Total leads scored"
          value={stats?.total_messages_scored ?? "—"}
          sub={`${stats?.notifications_sent ?? 0} alerts sent`}
        />
        <MetricCard
          testId="metric-groups"
          label="Groups monitored"
          value={`${stats?.groups_active ?? 0}/${stats?.groups_total ?? 0}`}
          sub={stats?.pushover_configured ? "Pushover ✓ configured" : "Pushover ✗ not configured"}
          accent={stats?.pushover_configured ? "success" : "warn"}
        />
        <MetricCard
          testId="metric-ai"
          label="AI calls · cost"
          value={`${ai.calls ?? 0}`}
          sub={`~$${(ai.cost_usd ?? 0).toFixed(4)} · ~€${(ai.cost_eur_est ?? 0).toFixed(4)} · ${
            (ai.input_tokens ?? 0) + (ai.output_tokens ?? 0)
          } tok`}
          accent="info"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Feed */}
        <div className="lg:col-span-3 border border-zinc-200 bg-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-4 border-b border-zinc-200">
            <div className="flex items-center gap-3">
              <ChatTeardropTextIcon size={18} weight="bold" />
              <h2 className="font-heading font-bold text-lg">Live feed</h2>
              <span className="overline">{messages.length} entries</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 sm:flex-none">
                <MagnifyingGlassIcon
                  size={14}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"
                />
                <Input
                  data-testid="feed-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search messages…"
                  className="pl-7 w-full sm:w-56"
                />
              </div>
              <Select
                data-testid="feed-priority-filter"
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                <option value="">All priorities</option>
                <option value="high">High only</option>
                <option value="medium">Medium only</option>
              </Select>
            </div>
          </div>

          <div className="divide-y divide-zinc-100 max-h-[calc(100vh-360px)] overflow-y-auto">
            {loading && messages.length === 0 && (
              <div className="p-12 text-center text-sm text-zinc-400">Loading feed…</div>
            )}
            {!loading && messages.length === 0 && (
              <div className="p-12 text-center text-sm text-zinc-400">
                No opportunities yet. Once the WhatsApp listener forwards messages, matched leads
                will appear here.
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                data-testid={`feed-item-${m.id}`}
                className="p-4 sm:p-5 hover:bg-zinc-50 transition-colors feed-enter"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0 order-2 sm:order-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="overline">{m.group_name}</span>
                      <span className="text-zinc-300">·</span>
                      <span className="text-xs text-zinc-500">{m.sender}</span>
                    </div>
                    <p className="text-sm text-zinc-950 leading-relaxed break-words">
                      {m.message}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-3">
                      {m.matched_keywords?.map((k) => (
                        <KeywordPill key={k}>{k}</KeywordPill>
                      ))}
                      {m.ai_classification && (
                        <span className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold bg-[#002FA7] text-white">
                          AI · {Math.round((m.ai_classification.confidence || 0) * 100)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex sm:flex-col items-center sm:items-end gap-2 flex-shrink-0 order-1 sm:order-2 flex-wrap">
                    <PriorityBadge priority={m.priority} testId={`badge-${m.id}`} />
                    <span className="font-mono-num text-xs text-zinc-500">
                      score {m.score}
                    </span>
                    <span className="font-mono-num text-[10px] text-zinc-400 hidden sm:block">
                      {formatTime(m.timestamp)} · {formatDate(m.timestamp)}
                    </span>
                    {m.notified === false && m.notify_error && (
                      <span className="text-[10px] text-[#FF3B30]" title={m.notify_error}>notify failed</span>
                    )}
                    {m.notified === false && !m.notify_error && (
                      <span className="text-[10px] text-zinc-400" title="Backfilled from history scan — intentionally not alerted (old message)">backfilled</span>
                    )}
                    <button
                      data-testid={`delete-message-${m.id}`}
                      onClick={() => onDelete(m.id)}
                      title="Delete this lead"
                      className="text-zinc-300 hover:text-[#FF3B30] transition-colors mt-1"
                    >
                      <TrashIcon size={15} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="flex flex-col gap-6">
          <div className="border border-zinc-200 bg-white p-6">
            <div className="overline mb-3">AI cost target</div>
            <div className="font-mono-num text-2xl font-semibold">
              €{(ai.cost_eur_est ?? 0).toFixed(4)}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              Target &lt; €5/month · GPT-4.1-mini
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="overline">Input tok</div>
                <div className="font-mono-num text-sm">{ai.input_tokens ?? 0}</div>
              </div>
              <div>
                <div className="overline">Output tok</div>
                <div className="font-mono-num text-sm">{ai.output_tokens ?? 0}</div>
              </div>
            </div>
          </div>

          <div className="border border-zinc-200 bg-white p-6">
            <div className="overline mb-3">Setup checklist</div>
            <ul className="text-xs space-y-2 text-zinc-700">
              <li className="flex gap-2">
                <span className={stats?.pushover_configured ? "text-emerald-600" : "text-[#FF3B30]"}>
                  {stats?.pushover_configured ? "✓" : "✗"}
                </span>
                Pushover configured
              </li>
              <li className="flex gap-2">
                <span className={stats?.push_configured ? "text-emerald-600" : "text-zinc-400"}>
                  {stats?.push_configured ? "✓" : "·"}
                </span>
                Web Push · {stats?.push_subscribers ?? 0} subscriber(s)
              </li>
              <li className="flex gap-2">
                <span className={(stats?.groups_total ?? 0) > 0 ? "text-emerald-600" : "text-zinc-400"}>
                  {(stats?.groups_total ?? 0) > 0 ? "✓" : "·"}
                </span>
                WhatsApp listener forwarding
              </li>
              <li className="flex gap-2">
                <span className="text-emerald-600">✓</span>
                Keyword engine active
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Shell>
  );
}
